/**
 * Type Definitions for Tasks Service
 *
 * Handles task scheduling and ordering for matters.
 * Tasks represent when to work on a matter (Today, Upcoming, etc.)
 */

import type { OutlookSendIdentity } from '@/lib/services/communications/types';
import type { PmsProvider } from '@/lib/services/integrations/pms';

// ============================================================================
// Core Task Types (Match Backend Exactly)
// ============================================================================

export interface Task {
  id: number;
  scheduledDate: string; // YYYY-MM-DD format (DATE type on backend)
  position?: number; // Decimal for drag-drop ordering (e.g., 1.0, 1.5, 2.0)
  status: TaskStatus;

  // Foreign keys
  lawFirmId?: number; // Law firm ID (from backend)
  lawFirmUserId?: number; // Assigned user ID (from backend)
  matterId?: number;

  // Relationships
  matter?: TaskMatter; // Embedded matter data
  lawFirmUser?: TaskUser; // Who the task is assigned to

  // Metadata
  // OPT: Normalise matter metadata
  description?: string | null;
  metadata?: {
    description?: string | null;
  };

  // Timestamps
  createdAt: Date | string;
  updatedAt: Date | string;
}

export type TaskStatus = 'active' | 'completed';

/**
 * Matter stage information (matches backend exactly)
 * Used for MatterProgress component
 *
 * Re-exported from the canonical definition in `lib/services/matters/types.ts`.
 */
import type {
  MatterStage,
  StageType,
  StageCompletionState,
  StageProgress
} from '@/lib/services/matters/types';

export type { MatterStage, StageType, StageCompletionState, StageProgress };

/**
 * User role on a matter
 */
export interface MatterRole {
  id: number;
  firstName: string | null;
  lastName: string | null;
  role: string; // "Acting solicitor", "Assisting member", etc.
}

/**
 * Acting solicitor information
 */
export interface ActingSolicitor {
  id: number;
  firstName: string | null;
  lastName: string | null;
}

/**
 * Legal Personal Representative information
 */
export interface LegalPersonalRepresentative {
  id: number;
  firstName: string | null;
  middleName?: string | null;
  lastName: string | null;
  isLegalPersonalRepresentative?: boolean; // Optional since MatterSummary might not include it
  legalPersonalRepresentativeRole?: string | null; // Optional since MatterSummary might not include it
}

/**
 * Matter data embedded in Task responses
 * Matches actual backend GET /tasks/today and GET /tasks/upcoming structure
 */
export interface TaskMatter {
  id: number;
  type: string; // Slug: "estate-administration", etc.
  typeLabel: string; // Display: "Estate Administration"
  description?: string | null;
  lawFirmInternalReference: string | null;
  decedentFirstName: string | null;
  decedentMiddleName?: string | null;
  decedentLastName: string | null;
  stages: MatterStage[]; // Progress stages for MatterProgress component
  actingSolicitor: ActingSolicitor | null; // Primary team member
  roles?: MatterRole[]; // All team members with roles (includes actingSolicitor)
  legalPersonalRepresentatives?: LegalPersonalRepresentative[]; // LPRs (may not be in all endpoints)
  tags?: string[]; // Matter tags (only in GET /tasks responses, not GET /matters)
  isArchived?: boolean;
  isDemo?: boolean;
  notes?: string; // Matter notes
  createdAt?: string; // Creation timestamp for sorting
  position?: string;
  lastUpdated?: string; // Last update timestamp for sorting
  // OPT: Normalise matter metadata with tasks
  metadata?: {
    clientNames?: string[]; // For non-estate matters (legacy)
    description?: string; // For non-estate matters (especially Business Law)
    pms?: {
      matterTypeLabel?: string;
    };
  };
  // Clients for non-estate-administration matters (new contact-based system)
  clients?: Array<{
    id: number;
    firstName?: string;
    lastName?: string;
    organisationName?: string | null;
  }>;
  /**
   * Whether this matter can send email at all, and from whose mailbox — the
   * backend's own send-time decision, carried on every matter row so the list
   * can flag a matter that cannot send before the user opens it (VALE-347).
   *
   * `null` for every firm not on Outlook transport, which is almost all of them.
   * Served by both `GET /matters` and `GET /tasks*`, so the row renders the same
   * whichever view it appears in.
   *
   * **Never re-derive this by comparing email addresses**, and render its
   * `blockedMessage` verbatim rather than switching on `blockedReason` — see
   * the note on `Matter.outlookSendIdentity`.
   */
  outlookSendIdentity?: OutlookSendIdentity | null;

  /**
   * Whether this matter is synced to the firm's CURRENT PMS, and which
   * provider — the backend's own `checkMatterPmsStatus` derivation, carried
   * on every matter row so the Today / Upcoming list can flag a synced matter
   * the same way `GET /matters` does (VALE-475).
   *
   * `pmsProviderName` is absent for every firm with no enabled PMS. Gate any
   * mark on `pmsSynced` alone — it is already scoped to the firm's *current*
   * integration, so a matter stranded on a since-retired provider reads
   * `false` here, never `true`.
   */
  pmsSynced?: boolean;
  pmsProviderName?: PmsProvider | 'leap' | 'clio';
}

/**
 * User data embedded in Task responses
 */
export interface TaskUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

// ============================================================================
// Request/Response Types
// ============================================================================

export interface CreateTaskInput {
  matterId: number;
  scheduledDate: string; // YYYY-MM-DD format
  position?: number; // Optional position for ordering
  description?: string;
  lawFirmUserId?: number; // User assigned to the task
}

export interface UpdateTaskInput {
  scheduledDate?: string; // YYYY-MM-DD format
  position?: number;
  status?: TaskStatus;
  description?: string;
}

export interface TaskFilters {
  lawFirmUserId?: number;
  tags?: string[]; // Filter tasks by matter tags
  includeCompleted?: boolean;
  page?: number;
  limit?: number;
}

// ============================================================================
// View Types for Homepage
// ============================================================================

export type ViewType = 'today' | 'upcoming' | 'anytime' | 'all' | 'archive';

export interface FilterState {
  tags: string[]; // Filter by matter tags
  types: string[]; // Filter by matter type slugs (e.g., 'estate-administration')
  teamMembers: string[]; // Filter by assigned user IDs (All/Archive only)
  /** Sort field - matches backend: updatedAt, createdAt, lawFirmInternalReference */
  sortBy?: 'updatedAt' | 'createdAt' | 'lawFirmInternalReference';
  /** Sort direction - matches backend: ASC, DESC */
  sortOrder?: 'ASC' | 'DESC';
  /** Transient search query — client-side for today/upcoming/anytime, backend `q` for all/archive */
  search?: string;
}

// ============================================================================
// Drag & Drop Types
// ============================================================================

export interface TaskPositionUpdate {
  taskId: number;
  position: number;
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Combined type for UI components that can receive either Task or Matter
 */
export type MatterWithTask = Task | TaskMatter;

/**
 * Helper to check if data is a Task (has 'matter' property)
 */
export function isTask(data: MatterWithTask): data is Task {
  return 'matter' in data;
}

/**
 * Helper to extract Matter from Task or return Matter directly
 * Returns null if matter is not available to prevent crashes
 */
export function extractMatter(data: MatterWithTask): TaskMatter | null {
  if (isTask(data)) {
    return data.matter || null;
  }
  return data || null;
}

/**
 * Helper to extract Task if present
 */
export function extractTask(data: MatterWithTask): Task | undefined {
  if (isTask(data)) {
    return data;
  }
  return undefined;
}

// ============================================================================
// Stage Transformation Utilities
// ============================================================================

/**
 * Stage status type expected by ListCard's MatterProgress component
 */
export type StageStatus = 'not-started' | 'in-progress' | 'completed';

/**
 * Stage format expected by ListCard's MatterProgress component
 */
export interface ListCardStage {
  name: string;
  status: StageStatus;
}

/**
 * Map backend completionState to ListCard StageStatus
 * Note: Backend uses 'complete' (not 'completed')
 */
export function mapCompletionStateToStageStatus(
  completionState: MatterStage['completionState']
): StageStatus {
  switch (completionState) {
    case 'complete':
      return 'completed';
    case 'in_progress':
      return 'in-progress';
    case 'not_started':
    case 'not_applicable':
      return 'not-started';
    default:
      // Defensive fallback for stale/unexpected persisted values (e.g. a
      // legacy fixture or import row) — never crash the render.
      return 'not-started';
  }
}

/**
 * Map backend stage slug to display name.
 *
 * Names must match the in-matter Matter Progress panel: the legacy persisted
 * slugs `representation` and `collection` display as "Grant" and "Redemption".
 */
export function mapStageSlugToName(stageSlug: string): string {
  const stageNames: Record<string, string> = {
    initial_setup: 'Initial Setup',
    discovery: 'Discovery',
    representation: 'Grant',
    collection: 'Redemption',
    distribution: 'Distribution',
    finalization: 'Finalization'
  };

  return stageNames[stageSlug] || stageSlug;
}

/**
 * Transform backend MatterStage[] to ListCard Stage[] format
 */
export function transformStagesToListCardFormat(
  backendStages: MatterStage[]
): ListCardStage[] {
  return backendStages.map((stage) => ({
    name: mapStageSlugToName(stage.stage),
    status: mapCompletionStateToStageStatus(stage.completionState)
  }));
}

/**
 * Calculate progress metrics from stages
 */
export function calculateStageProgress(stages: MatterStage[]): {
  total: number;
  completed: number;
} {
  const total = stages.length;
  const completed = stages.filter(
    (s) => s.completionState === 'complete'
  ).length;

  return { total, completed };
}

// ============================================================================
// Team Member Utilities
// ============================================================================

/**
 * Format team member name from firstName/lastName
 */
export function formatTeamMemberName(
  firstName: string | null,
  lastName: string | null
): string {
  const first = firstName?.trim() || '';
  const last = lastName?.trim() || '';

  if (first && last) return `${first} ${last}`;
  if (first) return first;
  if (last) return last;
  return 'Unknown';
}
